import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

// ── TypeScript Interfaces ─────────────────────────────────────

type BudgetSubmissionStatus = 'Draft' | 'Submitted' | 'InReview' | 'Approved' | 'Rejected';

interface BudgetSubmission {
  id: string;
  title: string;
  description: string;
  submitterId: string;
  subcommitteeId: string;
  eventId: string | null;
  projectName: string | null;
  status: BudgetSubmissionStatus;
  totalAmount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  subcommittee?: { id: string; name: string };
}

interface SubcommitteeOption {
  id: string;
  name: string;
}

interface EventOption {
  id: string;
  name: string;
}

interface BudgetCategoryOption {
  id: string;
  name: string;
}

interface LineItemForm {
  category: string;
  description: string;
  amount: string;
  justification: string;
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnSecondary: { padding: '0.5rem 1rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%' },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const, width: '100%' },
  textarea: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%', minHeight: '80px', resize: 'vertical' as const },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: '1rem' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  lineItemRow: { display: 'grid', gridTemplateColumns: '1fr 2fr 120px 2fr 40px', gap: '0.5rem', alignItems: 'start', marginBottom: '0.75rem' },
  totalBar: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem 0', borderTop: '2px solid #c9a84c', marginTop: '1rem' },
  totalAmount: { fontSize: '1.25rem', fontWeight: 700, color: '#1a6b3c' },
  totalLabel: { fontSize: '0.9rem', color: '#0f2d1f', marginRight: '0.75rem', fontWeight: 600 },
  radioGroup: { display: 'flex', gap: '1.5rem', alignItems: 'center', marginTop: '0.25rem' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', cursor: 'pointer' },
  removeBtn: { padding: '0.25rem 0.5rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '3px', fontSize: '0.75rem', cursor: 'pointer', lineHeight: 1 },
};

const STATUS_COLORS: Record<BudgetSubmissionStatus, string> = {
  Draft: '#666',
  Submitted: '#1a6b3c',
  InReview: '#c9a84c',
  Approved: '#2e7d32',
  Rejected: '#d32f2f',
};

// ── Helper Functions ──────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function createEmptyLineItem(): LineItemForm {
  return { category: '', description: '', amount: '', justification: '' };
}

// ── Main Component ────────────────────────────────────────────

export function BudgetSubmissionPage() {
  const { user } = useAuth();
  const { canEdit } = usePageAuthorization('budget-submissions');

  // View state: 'list' or 'form'
  const [view, setView] = useState<'list' | 'form'>('list');

  // List state
  const [submissions, setSubmissions] = useState<BudgetSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<BudgetSubmissionStatus | ''>('');
  const [searchText, setSearchText] = useState('');

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formSubcommitteeId, setFormSubcommitteeId] = useState('');
  const [formAssociationType, setFormAssociationType] = useState<'event' | 'project'>('event');
  const [formEventId, setFormEventId] = useState('');
  const [formProjectName, setFormProjectName] = useState('');
  const [lineItems, setLineItems] = useState<LineItemForm[]>([createEmptyLineItem()]);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Reference data
  const [subcommittees, setSubcommittees] = useState<SubcommitteeOption[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategoryOption[]>([]);
  const [categoriesError, setCategoriesError] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchSubmissions();
  }, []);

  async function fetchSubmissions() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<BudgetSubmission[]>('/budget-submissions');
      setSubmissions(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load budget submissions.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchFormData() {
    try {
      const [subs, evts] = await Promise.all([
        apiClient<SubcommitteeOption[]>('/subcommittees'),
        apiClient<EventOption[]>('/events'),
      ]);
      setSubcommittees(subs);
      setEvents(evts);
    } catch {
      // Non-critical; dropdowns will be empty
    }

    try {
      const cats = await apiClient<BudgetCategoryOption[]>('/budget-categories?active=true');
      setBudgetCategories(cats);
      setCategoriesError(false);
    } catch {
      setBudgetCategories([]);
      setCategoriesError(true);
    } finally {
      setCategoriesLoaded(true);
    }
  }

  // ── Form Handlers ─────────────────────────────────────────

  function handleNewSubmission() {
    resetForm();
    fetchFormData();
    setView('form');
  }

  function handleCancelForm() {
    setView('list');
    resetForm();
  }

  function resetForm() {
    setFormTitle('');
    setFormDescription('');
    setFormSubcommitteeId('');
    setFormAssociationType('event');
    setFormEventId('');
    setFormProjectName('');
    setLineItems([createEmptyLineItem()]);
    setFormErrors([]);
  }

  function handleAddLineItem() {
    setLineItems([...lineItems, createEmptyLineItem()]);
  }

  function handleRemoveLineItem(index: number) {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  }

  function handleLineItemChange(index: number, field: keyof LineItemForm, value: string) {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  }

  function computeTotal(): number {
    return lineItems.reduce((sum, item) => {
      const amount = parseFloat(item.amount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
  }

  function validateForm(): string[] {
    const errors: string[] = [];

    if (!formTitle.trim()) errors.push('Title is required');
    if (formTitle.trim().length > 200) errors.push('Title must not exceed 200 characters');
    if (!formDescription.trim()) errors.push('Description is required');
    if (!formSubcommitteeId) errors.push('Subcommittee is required');

    if (formAssociationType === 'event' && !formEventId) {
      errors.push('Please select an event');
    }
    if (formAssociationType === 'project' && !formProjectName.trim()) {
      errors.push('Project name is required');
    }

    if (lineItems.length === 0) {
      errors.push('At least one line item is required');
    }

    lineItems.forEach((item, idx) => {
      const num = idx + 1;
      if (!item.category.trim()) errors.push(`Line item ${num}: Category is required`);
      if (item.category.trim().length > 100) errors.push(`Line item ${num}: Category must not exceed 100 characters`);
      if (!item.description.trim()) errors.push(`Line item ${num}: Description is required`);
      if (item.description.trim().length > 500) errors.push(`Line item ${num}: Description must not exceed 500 characters`);
      const amount = parseFloat(item.amount);
      if (!item.amount.trim() || isNaN(amount) || amount <= 0) {
        errors.push(`Line item ${num}: Amount must be a positive number`);
      }
    });

    return errors;
  }

  async function handleSubmitForm(e: FormEvent) {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors([]);
    setSubmitting(true);
    setError('');

    try {
      await apiClient('/budget-submissions', {
        method: 'POST',
        body: {
          title: formTitle.trim(),
          description: formDescription.trim(),
          subcommitteeId: formSubcommitteeId,
          eventId: formAssociationType === 'event' ? formEventId : null,
          projectName: formAssociationType === 'project' ? formProjectName.trim() : null,
          lineItems: lineItems.map(item => ({
            category: item.category.trim(),
            description: item.description.trim(),
            amount: parseFloat(item.amount),
            justification: item.justification.trim() || null,
          })),
        },
      });
      setView('list');
      resetForm();
      await fetchSubmissions();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to create budget submission.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Filtering ─────────────────────────────────────────────

  const filteredSubmissions = submissions.filter(sub => {
    const matchesStatus = !statusFilter || sub.status === statusFilter;
    const matchesSearch = !searchText ||
      sub.title.toLowerCase().includes(searchText.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // ── Determine if user is a Chair (can create submissions) ─
  const isChair = user?.role === 'Organizer' || user?.role === 'Admin';

  // ── Render ────────────────────────────────────────────────

  if (view === 'form') {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>New Budget Submission</h1>

        {error && <div style={styles.error}>{error}</div>}
        {formErrors.length > 0 && (
          <div style={styles.error}>
            {formErrors.map((err, i) => <div key={i}>{err}</div>)}
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Submission Details</h2>
          <form onSubmit={handleSubmitForm}>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>Title *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="e.g., 2026 Venue Decorations Budget"
                  maxLength={200}
                  aria-label="Submission title"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Subcommittee *</label>
                <select
                  style={styles.select}
                  value={formSubcommitteeId}
                  onChange={e => setFormSubcommitteeId(e.target.value)}
                  aria-label="Subcommittee"
                >
                  <option value="">Select subcommittee...</option>
                  {subcommittees.map(sc => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Description *</label>
              <textarea
                style={styles.textarea}
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="Describe the purpose and justification for this budget..."
                aria-label="Description"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Association Type *</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="associationType"
                    value="event"
                    checked={formAssociationType === 'event'}
                    onChange={() => setFormAssociationType('event')}
                  />
                  Event
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="associationType"
                    value="project"
                    checked={formAssociationType === 'project'}
                    onChange={() => setFormAssociationType('project')}
                  />
                  Project
                </label>
              </div>
            </div>

            {formAssociationType === 'event' && (
              <div style={styles.field}>
                <label style={styles.label}>Event *</label>
                <select
                  style={styles.select}
                  value={formEventId}
                  onChange={e => setFormEventId(e.target.value)}
                  aria-label="Event"
                >
                  <option value="">Select event...</option>
                  {events.map(ev => (
                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                  ))}
                </select>
              </div>
            )}

            {formAssociationType === 'project' && (
              <div style={styles.field}>
                <label style={styles.label}>Project Name *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formProjectName}
                  onChange={e => setFormProjectName(e.target.value)}
                  placeholder="e.g., Community Outreach Initiative"
                  aria-label="Project name"
                />
              </div>
            )}

            {/* Line Items Section */}
            <div style={{ marginTop: '1.5rem' }}>
              <h2 style={styles.sectionTitle}>Line Items</h2>

              {/* Categories unavailable warning */}
              {categoriesLoaded && (categoriesError || budgetCategories.length === 0) && (
                <div style={styles.error}>
                  Budget categories are unavailable. Please try again later.
                </div>
              )}

              {/* Header row */}
              <div style={{ ...styles.lineItemRow, marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f2d1f' }}>Category *</span>
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f2d1f' }}>Description *</span>
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f2d1f' }}>Amount ($) *</span>
                <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f2d1f' }}>Justification</span>
                <span></span>
              </div>

              {lineItems.map((item, idx) => (
                <div key={idx} style={styles.lineItemRow}>
                  <select
                    style={styles.select}
                    value={item.category}
                    onChange={e => handleLineItemChange(idx, 'category', e.target.value)}
                    aria-label={`Line item ${idx + 1} category`}
                    disabled={!categoriesLoaded || categoriesError || budgetCategories.length === 0}
                  >
                    <option value="">Select category...</option>
                    {budgetCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <input
                    style={styles.input}
                    type="text"
                    value={item.description}
                    onChange={e => handleLineItemChange(idx, 'description', e.target.value)}
                    placeholder="Description"
                    maxLength={500}
                    aria-label={`Line item ${idx + 1} description`}
                  />
                  <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={item.amount}
                    onChange={e => handleLineItemChange(idx, 'amount', e.target.value)}
                    placeholder="0.00"
                    aria-label={`Line item ${idx + 1} amount`}
                  />
                  <input
                    style={styles.input}
                    type="text"
                    value={item.justification}
                    onChange={e => handleLineItemChange(idx, 'justification', e.target.value)}
                    placeholder="Justification (optional)"
                    aria-label={`Line item ${idx + 1} justification`}
                  />
                  <button
                    type="button"
                    style={styles.removeBtn}
                    onClick={() => handleRemoveLineItem(idx)}
                    disabled={lineItems.length <= 1}
                    aria-label={`Remove line item ${idx + 1}`}
                    title="Remove line item"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                style={styles.btnGold}
                onClick={handleAddLineItem}
                disabled={!categoriesLoaded || categoriesError || budgetCategories.length === 0}
              >
                + Add Line Item
              </button>

              {/* Running Total */}
              <div style={styles.totalBar}>
                <span style={styles.totalLabel}>Total:</span>
                <span style={styles.totalAmount}>{formatCurrency(computeTotal())}</span>
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button
                type="submit"
                style={styles.btn}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Budget'}
              </button>
              <button
                type="button"
                style={styles.btnSecondary}
                onClick={handleCancelForm}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ ...styles.title, margin: 0 }}>Budget Submissions</h1>
        {isChair && canEdit && (
          <button style={styles.btn} onClick={handleNewSubmission}>
            + New Submission
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.section}>
        <div style={styles.filterBar}>
          <input
            style={{ ...styles.input, width: '250px' }}
            type="text"
            placeholder="Search by title..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            aria-label="Search submissions by title"
          />
          <select
            style={{ ...styles.select, width: '180px' }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as BudgetSubmissionStatus | '')}
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="InReview">In Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading submissions...</div>
        ) : filteredSubmissions.length === 0 ? (
          <div style={styles.emptyState}>
            {submissions.length === 0
              ? 'No budget submissions yet.'
              : 'No submissions match your filters.'}
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Subcommittee</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Total Amount</th>
                  <th style={styles.th}>Submitted</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map(sub => (
                  <tr key={sub.id}>
                    <td style={styles.td}>{sub.title}</td>
                    <td style={styles.td}>{sub.subcommittee?.name || '—'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: STATUS_COLORS[sub.status] }}>
                        {sub.status === 'InReview' ? 'In Review' : sub.status}
                      </span>
                    </td>
                    <td style={styles.td}>{formatCurrency(sub.totalAmount)}</td>
                    <td style={styles.td}>{formatDate(sub.submittedAt)}</td>
                    <td style={styles.td}>
                      {canEdit && sub.status === 'Draft' && (
                        <button
                          style={styles.btn}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await apiClient(`/budget-submissions/${sub.id}/submit`, { method: 'PATCH' });
                              fetchSubmissions();
                            } catch (err: unknown) {
                              const apiErr = err as { message?: string };
                              setError(apiErr.message || 'Failed to submit for review.');
                            }
                          }}
                        >
                          Submit for Review
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
